import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/panel";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Description,
  FieldError,
  FieldGroup,
  Input,
  Label,
  TextArea,
  TextField,
  UnstyledInput,
} from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { PanelDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof createSchema>;

export function PanelDetailForm({ panel }: { panel?: PanelDetail }) {
  const create = trpc.admin.panel.create.useMutation();
  const update = trpc.admin.panel.update.useMutation();

  const navigate = useNavigate();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      ...panel,
      code: panel?.code ?? "",
      description: panel?.description ?? "",
      price: panel?.price.toNumber(),
      name: panel?.name || "",
      billingMode: panel?.billingMode || "PER_ACCESSION",
    },
    resolver: zodResolver(createSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (panel) {
      update.mutate(
        { id: panel.id, ...values },
        mutationResponse({
          errorTitle: "Error updating panel",
          successMessage: "Panel updated",
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding panel",
          successMessage: "Panel added",
          onSuccess(data) {
            navigate({
              to: "/admin/panels/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="panel-name"
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Name</Label>
                <Input />
                <Description>Display name for the panel.</Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="code"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="panel-code"
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Code</Label>
                <Input />
                <Description>Optional internal code for the panel.</Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="panel-description"
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
                className="md:col-span-2"
              >
                <Label>Description</Label>
                <TextArea rows={3} />
                <Description>
                  Optional description for additional admin context.
                </Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="price"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                // prefix="$"
                // suffix="USD"
                value={field.value?.toString()}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Price</Label>
                <FieldGroup className="px-2">
                  <Text muted>$</Text>
                  <UnstyledInput placeholder="0.00" />
                  <Text muted>USD</Text>
                </FieldGroup>
                <Description>Base price for the panel.</Description>
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="billingMode"
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Billing mode</Label>
                <SelectTrigger />
                <SelectList>
                  <SelectItem id="PER_ACCESSION">Bill per accession</SelectItem>
                  <SelectItem id="PER_SAMPLE">Bill per sample</SelectItem>
                </SelectList>
                <Description>How the panel should be billed.</Description>
                <FieldError>{error?.message}</FieldError>
              </Select>
            )}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Section.Subtitle>Options</Section.Subtitle>
          <Controller
            control={control}
            name="enabled"
            render={({ field, fieldState: { error } }) => (
              <CheckboxField>
                <Checkbox
                  id="check-enabled"
                  isSelected={field.value}
                  onChange={(val) => field.onChange(!!val)}
                  onBlur={field.onBlur}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  Enabled
                </Checkbox>
                <Description>
                  Enable panel for selection during order entry
                </Description>
              </CheckboxField>
            )}
          />
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Link to="/admin/panels" variant="outline" asButton>
            Cancel
          </Link>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
          >
            Save
          </Button>
        </div>
      </AdminForm>
    </TabLayout>
  );
}
